import api from '@pray/shared/utils/api';

import { mapClipDetail, mapClipsPage } from './mappers';
import type { ClipDetailResponse, ClipsListResponse, ClipsQuery } from './types';

const clipsUrl = (artistId: string) => `/web/studio/artists/${artistId}/clips`;

const requireId = (value: string | undefined, name: string) => {
  if (!value) throw Error(`${name} is required`);
};

/** STUDIO_API.md's query keys; empty values are left off so the server applies no filter (or its default sort). */
export function toClipsQueryParams(query: ClipsQuery): Record<string, string | number> {
  const params: Record<string, string | number> = { limit: query.limit ?? 25 };
  const entries: [string, string | null | undefined][] = [
    ['type', query.type],
    ['status', query.status],
    ['contentId', query.contentId],
    ['from', query.from],
    ['to', query.to],
    ['q', query.q?.trim()],
    ['sort', query.sort],
    ['cursor', query.cursor],
  ];
  entries.forEach(([key, value]) => {
    if (value) params[key] = value;
  });
  return params;
}

export async function getClips({ artistId, query = {} }: { artistId: string; query?: ClipsQuery }) {
  requireId(artistId, 'Artist ID');
  const response = await api.executeGetRequest<{ data: ClipsListResponse }>(
    clipsUrl(artistId),
    toClipsQueryParams(query)
  );
  return mapClipsPage(response.data.data);
}

export async function getClip({ artistId, clipId }: { artistId: string; clipId: string }) {
  requireId(artistId, 'Artist ID');
  requireId(clipId, 'Clip ID');
  const response = await api.executeGetRequest<{ data: ClipDetailResponse }>(`${clipsUrl(artistId)}/${clipId}`);
  return mapClipDetail(response.data.data);
}

/** Soft-deletes the clip (409 CLIP_IN_USE while a post is scheduled or it is being made); PPS deletes its copy too. */
export async function deleteClip({ artistId, clipId }: { artistId: string; clipId: string }) {
  requireId(artistId, 'Artist ID');
  requireId(clipId, 'Clip ID');
  await api.executeDeleteRequest(`${clipsUrl(artistId)}/${clipId}`);
}
